import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import DialogFooter from '../DialogFooter';

type VisibleOverlay = {
  id: symbol;
  externalTrigger: HTMLElement | null;
  focusInside: () => void;
};

const visibleOverlayStack: VisibleOverlay[] = [];

type FullScreenConfirmOverlayProps = {
  show: boolean;
  title: string;
  message: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  cancelHidden?: boolean;
  cancelLabel?: string;
  confirmLabel?: string;
  busy?: boolean;
  confirmDisabled?: boolean;
  className?: string;
  width?: number;
  testId?: string;
};

export default function FullScreenConfirmOverlay({
  show,
  title,
  message,
  onCancel,
  onConfirm,
  cancelHidden = false,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  busy = false,
  confirmDisabled = false,
  className,
  width = 400,
  testId
}: FullScreenConfirmOverlayProps) {
  const overlayIdRef = useRef(Symbol('confirm-overlay'));
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const busyRef = useRef(busy);
  const onCancelRef = useRef(onCancel);
  busyRef.current = busy;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!show) return;

    const overlayId = overlayIdRef.current;
    const getFocusableElements = () => Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || [],
    );
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const overlayEntry: VisibleOverlay = {
      id: overlayId,
      externalTrigger: visibleOverlayStack[0]?.externalTrigger || previousFocus,
      focusInside: () => {
        (getFocusableElements()[0] || dialogRef.current)?.focus();
      },
    };
    visibleOverlayStack.push(overlayEntry);

    const onKeyDown = (e: KeyboardEvent) => {
      if (visibleOverlayStack[visibleOverlayStack.length - 1]?.id !== overlayId) return;
      if (e.key === 'Escape' && !busyRef.current) {
        onCancelRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        e.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const focusIsInside = dialogRef.current?.contains(document.activeElement);
      if (!focusIsInside) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    overlayEntry.focusInside();
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      const overlayIndex = visibleOverlayStack.findIndex((entry) => entry.id === overlayId);
      const wasTopmost = overlayIndex === visibleOverlayStack.length - 1;
      if (overlayIndex >= 0) visibleOverlayStack.splice(overlayIndex, 1);
      if (!wasTopmost) return;

      const remainingOverlay = visibleOverlayStack[visibleOverlayStack.length - 1];
      if (remainingOverlay) {
        remainingOverlay.focusInside();
      } else if (overlayEntry.externalTrigger?.isConnected) {
        overlayEntry.externalTrigger.focus();
      }
    };
  }, [show]);

  if (!show) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      data-testid={testId || undefined}
      onMouseDown={busy ? undefined : onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16
      }}
    >
      <div
        className={`drawer-controls-card${className ? ` ${className}` : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width, maxWidth: 'calc(100vw - 32px)', alignSelf: 'center' }}
      >
        <div className="stack" style={{ gap: 12 }}>
          <div className="modal-title">{title}</div>
          <div>{message}</div>
          <DialogFooter>
            {cancelHidden ? '' : (
            <button className="button secondary" onClick={onCancel} disabled={busy} data-testid={testId ? `${testId}-cancel` : undefined}>
              {cancelLabel}
            </button>
            )}
            <button className="button" onClick={onConfirm} disabled={busy || confirmDisabled} data-testid={testId ? `${testId}-confirm` : undefined}>
              {confirmLabel}
            </button>
          </DialogFooter>
        </div>
      </div>
    </div>,
    document.body
  );
}
